import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { actions as localeActions, languageSelector } from 'redux/modules/locale';
import { isUserClient } from 'selectors/user/user-roles-selector';
import { trackEvent, trackedEvents } from 'telemetry/app-analytics';
import enLogo from 'translation/en.png';
import ruLogo from 'translation/ru.png';
import zhLogo from 'translation/zh.png';
import { useOutsideClick } from 'utils/hooks/use-outside-click';

import styles from './language.module.css';

type Language = 'en' | 'ru' | 'zh';

const LANGUAGES: Language[] = ['en', 'ru', 'zh'];

const LANGUAGE_TITLES: Record<Language, string> = {
    en: 'English',
    ru: 'Русский',
    zh: '简体中文'
};

const LANGUAGE_LOGOS: Record<Language, string> = {
    en: enLogo,
    ru: ruLogo,
    zh: zhLogo
};

/** The language switcher of the header (not for clients): the flag opens the list of the languages */
const LanguageSwitcher = () => {
    const dispatch = useDispatch();
    const language: Language = useSelector(languageSelector);
    const userIsClient: boolean = useSelector(isUserClient);
    const [isOpened, setIsOpened] = useState(false);
    const switcherRef = useRef<HTMLDivElement>(null);
    const close = useCallback(() => setIsOpened(false), []);
    // The flag is inside: a click on it toggles the list, a click elsewhere closes it
    useOutsideClick({ ref: switcherRef, callback: close, isEnabled: isOpened, capture: true });

    const selectLanguage = (lang: Language) => {
        trackEvent(trackedEvents.SWITCH_LANG, { properties: { language: lang } });
        dispatch(localeActions.changeLanguage(lang));
        setIsOpened(false);
    };

    if (userIsClient) {
        return null;
    }

    const flag = (
        <span className={styles.base} onClick={() => setIsOpened(opened => !opened)}>
            <img alt="" src={LANGUAGE_LOGOS[language]} className={styles.langLogo} />
        </span>
    );

    if (!isOpened) {
        return flag;
    }

    return (
        <div className={styles.container} ref={switcherRef}>
            {flag}
            <div className={styles.dropdown}>
                <div className={styles.langSelect}>
                    {LANGUAGES.map(lang => (
                        <div
                            key={lang}
                            className={language === lang ? styles.currentLangItem : styles.langItem}
                            onClick={() => selectLanguage(lang)}
                        >
                            <img alt="" src={LANGUAGE_LOGOS[lang]} className={styles.langLogoItem} />
                            {LANGUAGE_TITLES[lang]}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default LanguageSwitcher;
